import { useMemo } from 'react';
import { isMobile } from 'react-device-detect';
import { Page } from '~/lib';
import { Frame } from '~/shared/utils';
import {
    _getBasePrice,
    _getGTMAffiliation,
    _getTotalValue,
    _transformGAEvent,
    _resolvePromotionEcommerce,
} from './helper';
import {
    GTM_EVENT,
    GTMProductEventModel,
    GTMBasketItemModel,
    GTMOverlaySearchModel,
    GTMAlgoliaSearchModel,
    GTMStorePickupDrawerOpenModel,
    GTMStorePickupDrawerSelectModel,
    GTMStorePickupDrawerToggleModel,
    GTMStorePickupDrawerAccordionModel,
    GTMStorePickupDrawerQuantityModel,
    GTMStorePickupDrawerLocationSearchModel,
    GTMUsedStorePickupOptionModel,
    StorePickupOption,
} from './models';
import { PromotionEvent, WishListEventData } from '../models';
import { sendGTMEvent } from '@next/third-parties/google';
import { trackingStore } from '../trackingStore';
import { formatUserData, GTMUserData } from '../getGtmUserData';
import { getGTMFilterData, GTMFilterData } from '../getGtmFilterData';

export const useGTMEvents = (frame?: Frame | null) => {
    // GTM Checkout event step 1
    function gtmViewCart(items: GTMProductEventModel[], value: string) {
        sendGTMEvent({
            event: GTM_EVENT.VIEW_CART,
            ecommerce: {
                currency: frame?.market.currency?.currency ?? '',
                items,
                value,
            },
        });
    }

    // GTM Checkout event step 2
    function gtmBeginCheckout(items: GTMProductEventModel[], value: string, coupon: string) {
        console.debug('gtmBeginCheckout', items, value, coupon);

        sendGTMEvent({
            event: GTM_EVENT.BEGIN_CHECKOUT,
            ecommerce: {
                coupon,
                currency: frame?.market.currency?.currency ?? '',
                items,
                value,
            },
        });
    }

    // GTM Checkout event step 3
    function gtmAddShippingInfo(
        items: GTMProductEventModel[],
        value: string,
        coupon: string,
        shipping_tier: string,
    ) {
        sendGTMEvent({
            event: GTM_EVENT.ADD_SHIPPING_INFO,
            ecommerce: {
                coupon,
                currency: frame?.market.currency?.currency ?? '',
                items,
                shipping_tier,
                value,
            },
        });
    }

    // GTM Checkout event step 4
    function gtmAddPaymentInfo(
        items: GTMProductEventModel[],
        value: string,
        coupon: string,
        payment_type: string | undefined,
    ) {
        sendGTMEvent({
            event: GTM_EVENT.ADD_PAYMENT_INFO,
            ecommerce: {
                coupon,
                currency: frame?.market.currency?.currency ?? '',
                items,
                payment_type,
                value,
            },
        });
    }

    // GTM Add coupon event
    function gtmAddCouponCode(items: GTMProductEventModel[], value: string, coupon: string) {
        sendGTMEvent({
            event: GTM_EVENT.ADD_COUPON,
            ecommerce: {
                coupon,
                currency: frame?.market.currency?.currency ?? '',
                items,
                value,
            },
        });
    }

    // GTM Add coupon event
    function gtmRemoveCouponCode(items: GTMProductEventModel[], value: string, coupon: string) {
        sendGTMEvent({
            event: GTM_EVENT.REMOVE_COUPON,
            ecommerce: {
                coupon,
                currency: frame?.market.currency?.currency ?? '',
                items,
                value,
            },
        });
    }

    // GTM Add gift card event
    function gtmAddGiftCard(items: GTMProductEventModel[], value: string, coupon: string) {
        sendGTMEvent({
            event: GTM_EVENT.ADD_GIFT_CARD,
            ecommerce: {
                coupon,
                currency: frame?.market.currency?.currency ?? '',
                items,
                value,
            },
        });
    }

    // GTM Remove gift card event
    function gtmRemoveGiftCard(items: GTMProductEventModel[], value: string, coupon: string) {
        sendGTMEvent({
            event: GTM_EVENT.REMOVE_GIFT_CARD,
            ecommerce: {
                coupon,
                currency: frame?.market.currency?.currency ?? '',
                items,
                value,
            },
        });
    }

    function gtmAddReward(rewardCode: string) {
        sendGTMEvent({
            event: GTM_EVENT.ADD_REWARD,
            ecommerce: {
                rewardCode,
            },
        });
    }

    function gtmRemoveReward(rewardCode: string) {
        sendGTMEvent({
            event: GTM_EVENT.REMOVE_REWARD,
            ecommerce: {
                rewardCode,
            },
        });
    }

    function gtmReserveVoucher(promoId: string) {
        sendGTMEvent({
            event: GTM_EVENT.RESERVE_VOUCHER,
            ecommerce: {
                promoId,
            },
        });
    }

    function gtmReleaseVoucher(promoId: string) {
        sendGTMEvent({
            event: GTM_EVENT.RELEASE_VOUCHER,
            ecommerce: {
                promoId,
            },
        });
    }

    // GTM Purchase event
    function gtmPurchase(
        affiliationOptions: {
            storeId: string | undefined;
            storeName: string | undefined;
            isLoggedIn: boolean | undefined;
            isStorePersonnel: boolean | undefined;
            paymentMethod: string | undefined;
        },
        coupon: string,
        transaction_id: string,
        items: GTMProductEventModel[],
        value: number,
        shipping: number,
    ) {
        const affiliation = _getGTMAffiliation(affiliationOptions);
        const itemsWithAffiliation = items.map((item) => ({
            ...item,
            affiliation,
        }));

        sendGTMEvent({
            event: GTM_EVENT.PURCHASE,
            ecommerce: {
                transaction_affiliation: affiliation,
                coupon,
                currency: frame?.market.currency?.currency ?? '',
                items: itemsWithAffiliation,
                shipping,
                transaction_id,
                value,
            },
        });
    }

    // GTM Remove from cart event
    function gtmRemoveFromCart(
        items: GTMProductEventModel[],
        quantity = 1,
        basketID?: string,
        basketContent?: GTMBasketItemModel[],
    ) {
        const value = _getBasePrice(items) * quantity;
        sendGTMEvent({
            event: GTM_EVENT.REMOVE_FROM_CART,
            basketID,
            basketContent,
            ecommerce: {
                currency: frame?.market.currency?.currency,
                value,
                items,
            },
        });
    }

    type AddToCartOptionsType = {
        items: GTMProductEventModel[];
        quantity?: number;
        basketContent?: GTMBasketItemModel[];
        basketID?: string;
        context?: StorePickupOption.ClickAndCollect;
    };

    // GTM add to cart event
    function gtmAddToCart({
        items,
        quantity = 1,
        basketContent,
        basketID,
        context,
    }: AddToCartOptionsType) {
        const value = _getBasePrice(items) * quantity;
        sendGTMEvent({
            event: GTM_EVENT.ADD_TO_CART,
            ...(context && { context }),
            basketID,
            basketContent,
            ecommerce: {
                currency: frame?.market.currency?.currency,
                value,
                ...(context && { item_list_name: context }),
                items,
            },
        });
    }

    // GTM view item event
    function gtmViewItem(items: GTMProductEventModel[], breadcrumb: string) {
        sendGTMEvent({
            event: GTM_EVENT.VIEW_ITEM,
            breadcrumb,
            ecommerce: {
                currency: frame?.market.currency?.currency,
                value: _getTotalValue(items),
                items,
            },
        });
    }

    function gtmReservation(
        items: GTMProductEventModel[],
        storeId?: string,
        reservationNumber?: string,
    ) {
        sendGTMEvent({
            event: GTM_EVENT.PRODUCT_RESERVATION_COMPLETE,
            reservation_number: reservationNumber,
            product: {
                currency: frame?.market.currency?.currency,
                value: _getTotalValue(items),
                shop_id: storeId,
                items,
            },
        });
    }

    // GTM select item event
    function gtmSelectItem(items: GTMProductEventModel[], raptorModule?: string) {
        sendGTMEvent({ ecommerce: null });
        sendGTMEvent({
            event: GTM_EVENT.SELECT_ITEM,
            ...(raptorModule && { raptorModule }),
            ecommerce: {
                currency: frame?.market.currency?.currency,
                items,
            },
        });
    }

    function gtmSelectPromotion(promotionDetails: PromotionEvent, items?: GTMProductEventModel[]) {
        sendGTMEvent({ ecommerce: null });
        sendGTMEvent({
            event: GTM_EVENT.SELECT_PROMOTION,
            ecommerce: _resolvePromotionEcommerce(promotionDetails, items),
        });
    }

    function gtmViewPromotion(promotionDetails: PromotionEvent, items?: GTMProductEventModel[]) {
        sendGTMEvent({ ecommerce: null });
        sendGTMEvent({
            event: GTM_EVENT.VIEW_PROMOTION,
            ecommerce: _resolvePromotionEcommerce(promotionDetails, items),
        });
    }

    // GTM item view list vvent
    async function gtmProductListView(items: GTMProductEventModel[]) {
        sendGTMEvent({
            event: GTM_EVENT.VIEW_ITEM_LIST,
            ecommerce: {
                items,
            },
        });
    }

    // GTM wishcloud button click event
    function gtmClickWishCloudButton(items: GTMProductEventModel[], breadcrumb: string) {
        sendGTMEvent({
            event: GTM_EVENT.CLICK_WISHCLOUD_BUTTON,
            breadcrumb,
            ecommerce: {
                currency: frame?.market.currency?.currency,
                value: _getTotalValue(items),
                items,
            },
        });
    }

    // GTM wishcloud error event
    function gtmErrorWishCloud() {
        sendGTMEvent({
            event: GTM_EVENT.ERROR_WISHCLOUD,
        });
    }

    //GTM Error events
    function gtmErrorEvent(err: string, innerError: Error) {
        sendGTMEvent({
            event: err,
            description: innerError,
        });
    }

    // GTM overlay search
    function gtmOverlaySearch(params: GTMOverlaySearchModel) {
        sendGTMEvent({
            event: 'SearchOverlayResults',
            SearchOverlayMeasurement: params,
        });
    }

    // Relays the products search queryID so GTM can attach it to Algolia Insights events
    function gtmAlgoliaSearch(result: GTMAlgoliaSearchModel, filters: string[]) {
        sendGTMEvent({
            event: GTM_EVENT.ALGOLIA_SEARCH,
            algolia_index_name: result.index,
            algolia_index_used: result.indexUsed ?? result.index,
            algolia_query: result.query,
            algolia_query_id: result.queryID,
            algolia_nb_hits: result.nbHits,
            algolia_filters: filters,
        });
    }

    type gtmInitGPageProps = {
        customerId: string | undefined;
        page: Page;
        loggedIn: boolean;
    };

    function gtmInitGPage({ customerId, page, loggedIn }: gtmInitGPageProps) {
        const algoliaId = trackingStore.getState().userToken;
        const pageData = {
            event: 'pageview',
            userID: customerId,
            countrySite: page.market.toUpperCase(),
            loggedIn: loggedIn.toString(),
            environment: frame?.settings?.googleTagManagerEnvironment || '',
            pageCategory: page.type,
            // The type of device being used by the user
            device: isMobile ? 'mobile' : 'desktop',
            // The hostname of the page
            hostname: window.location.hostname,
            // First url when opened the site
            originalLocation: sessionStorage.getItem('originalLocation'),
            pagePath: window.location.pathname + window.location.search,
            ...(algoliaId && { algolia_id: algoliaId }),
        };
        sendGTMEvent(pageData);
    }

    //GTM for user data
    async function gtmUserData(data: GTMUserData) {
        const userData = formatUserData(data);

        const pageData = {
            event: 'user_data_available',
            ...userData,
        };
        sendGTMEvent(pageData);
    }

    async function gtmFilterTracking(data: GTMFilterData) {
        const filterData = getGTMFilterData(data);
        const pageData = {
            event: 'filter_interaction',
            ...filterData,
        };
        sendGTMEvent(pageData);
    }

    function gaFilter(eventAction: string, eventLabel: string) {
        sendGTMEvent(_transformGAEvent('Filter', eventAction, eventLabel));
    }

    function gaProductPage(eventAction: string, eventLabel: string) {
        sendGTMEvent(_transformGAEvent('ProductPage', eventAction, eventLabel));
    }

    function gtmStorePickupDrawerOpen({
        context,
        option,
        availableStoresCr,
        availableStoresCc,
        totalStores,
    }: GTMStorePickupDrawerOpenModel) {
        sendGTMEvent({
            event: GTM_EVENT.OPEN_MODAL,
            context,
            option,
            available_stores_cr: availableStoresCr,
            available_stores_cc: availableStoresCc,
            total_stores: totalStores,
        });
    }

    function gtmStorePickupDrawerSelect({
        context,
        option,
        shopId,
    }: GTMStorePickupDrawerSelectModel) {
        sendGTMEvent({
            event: GTM_EVENT.SELECT_STORE,
            context,
            option,
            shop_id: shopId,
        });
    }

    function gtmStorePickupDrawerToggle({
        context,
        option,
        mode,
        availableStoresCr,
        availableStoresCc,
        totalStores,
    }: GTMStorePickupDrawerToggleModel) {
        sendGTMEvent({
            event: GTM_EVENT.SWITCH_TOGGLE,
            context,
            option,
            name: 'stores_with_product_in_stock',
            mode,
            available_stores_cr: availableStoresCr,
            available_stores_cc: availableStoresCc,
            total_stores: totalStores,
        });
    }

    function gtmStorePickupDrawerAccordion({
        context,
        option,
        shopId,
        index,
    }: GTMStorePickupDrawerAccordionModel) {
        sendGTMEvent({
            event: GTM_EVENT.OPEN_ACCORDION,
            context,
            option,
            type: 'shop_details',
            shop_id: shopId,
            index,
        });
    }

    function gtmStorePickupDrawerLocationSearch(model: GTMStorePickupDrawerLocationSearchModel) {
        sendGTMEvent({
            event: GTM_EVENT.LOCATION_SEARCH,
            context: model.context,
            option: model.option,
            method: model.method,
            ...(model.method === 'manual_input' && { input_type: model.inputType }),
        });
    }

    function gtmUsedStorePickupOption({ context, shopId }: GTMUsedStorePickupOptionModel) {
        sendGTMEvent({
            event: GTM_EVENT.USED_STORE_PICKUP_OPTION,
            context,
            shop_id: shopId,
        });
    }

    function gtmStorePickupDrawerQuantity({
        context,
        option,
        oldQuantity,
        newQuantity,
        method,
        availableStoresCr,
        availableStoresCc,
        totalStores,
    }: GTMStorePickupDrawerQuantityModel) {
        sendGTMEvent({
            event: GTM_EVENT.SELECT_QUANTITY,
            context,
            option,
            old_quantity: oldQuantity,
            new_quantity: newQuantity,
            method,
            available_stores_cr: availableStoresCr,
            available_stores_cc: availableStoresCc,
            total_stores: totalStores,
        });
    }

    function gaInternalSearch(eventLabel: string) {
        sendGTMEvent(_transformGAEvent('Search', 'Search Results - Zero Results', eventLabel));
    }

    function gaAuthenticationEvents(eventAction: string, eventLabel: string | undefined) {
        sendGTMEvent(_transformGAEvent('Login', eventAction, eventLabel ?? '/'));
    }

    function gaWishlistEvents({
        eventAction,
        eventLabel = '',
        productId,
        brand,
        name,
    }: WishListEventData) {
        const gaWishListData = {
            event: 'gaEvent',
            eventCategory: 'Wishlist',
            eventAction,
            eventLabel,
            productId,
            brandId: brand,
            productName: name,
        };
        sendGTMEvent(gaWishListData);
    }

    return useMemo(() => {
        return {
            gtmPurchase,
            gtmViewCart,
            gtmBeginCheckout,
            gtmAddShippingInfo,
            gtmAddPaymentInfo,
            gtmAddCouponCode,
            gtmRemoveCouponCode,
            gtmAddGiftCard,
            gtmRemoveGiftCard,
            gtmRemoveFromCart,
            gtmAddToCart,
            gtmViewItem,
            gtmReservation,
            gtmSelectItem,
            gtmSelectPromotion,
            gtmViewPromotion,
            gtmProductListView,
            gtmAlgoliaSearch,
            gtmInitGPage,
            gaFilter,
            gaProductPage,
            gtmStorePickupDrawerOpen,
            gtmStorePickupDrawerSelect,
            gtmStorePickupDrawerToggle,
            gtmStorePickupDrawerAccordion,
            gtmStorePickupDrawerQuantity,
            gtmStorePickupDrawerLocationSearch,
            gtmUsedStorePickupOption,
            gaInternalSearch,
            gaAuthenticationEvents,
            gaWishlistEvents,
            gtmClickWishCloudButton,
            gtmOverlaySearch,
            gtmErrorWishCloud,
            gtmAddReward,
            gtmRemoveReward,
            gtmReserveVoucher,
            gtmReleaseVoucher,
            gtmErrorEvent,
            gtmUserData,
            gtmFilterTracking,
        };
    }, [frame]);
};
